import type { ReactNode } from 'react';
import { memo, useMemo } from 'react';
import noop from 'lodash/noop';
import { createContext, useContextSelector } from 'use-context-selector';
import { useSearchHistory } from '../hooks/useSearchHistory';
import type { SearchHistory, SearchHistoryItem } from '../types';

type SearchHistoryState = {
  addRecentSearch: (search: SearchHistoryItem) => void;
  clearSearchHistory: () => void;
  hasSearchHistory: boolean;
  removeRecentSearch: (search: SearchHistoryItem) => void;
  searchHistory: SearchHistory;
};

const defaultDataState: SearchHistoryState = {
  addRecentSearch: noop,
  clearSearchHistory: noop,
  hasSearchHistory: false,
  removeRecentSearch: noop,
  searchHistory: [],
};

export const SearchHistoryContext = createContext(defaultDataState);

export const SearchHistoryProvider = memo(
  ({ children }: { children: ReactNode }) => {
    const {
      addRecentSearch,
      clearSearchHistory,
      removeRecentSearch,
      searchHistory,
    } = useSearchHistory();

    const hasSearchHistory = searchHistory.length > 0;

    const value = useMemo(
      () => ({
        addRecentSearch,
        clearSearchHistory,
        hasSearchHistory,
        removeRecentSearch,
        searchHistory,
      }),
      [
        addRecentSearch,
        clearSearchHistory,
        hasSearchHistory,
        removeRecentSearch,
        searchHistory,
      ]
    );

    return (
      <SearchHistoryContext.Provider value={value}>
        {children}
      </SearchHistoryContext.Provider>
    );
  }
);

export const useSearchHistoryData = () => {
  const addRecentSearch = useContextSelector(
    SearchHistoryContext,
    (value) => value?.addRecentSearch
  );

  const clearSearchHistory = useContextSelector(
    SearchHistoryContext,
    (value) => value?.clearSearchHistory
  );

  const hasSearchHistory = useContextSelector(
    SearchHistoryContext,
    (value) => value?.hasSearchHistory
  );

  const removeRecentSearch = useContextSelector(
    SearchHistoryContext,
    (value) => value?.removeRecentSearch
  );

  const searchHistory = useContextSelector(
    SearchHistoryContext,
    (value) => value?.searchHistory
  );

  return {
    addRecentSearch,
    clearSearchHistory,
    hasSearchHistory,
    removeRecentSearch,
    searchHistory,
  };
};
